import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { PORTFOLIO } from "@/data/company";

const SECTORS = ["All", "Fintech", "Healthcare", "Agriculture", "Real Estate", "Clean Energy"];

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Growth: "bg-slate-100 text-slate-900 border-slate-300",
  Development: "bg-amber-50 text-amber-900 border-amber-200",
  Hold: "bg-slate-50 text-slate-500 border-slate-200",
};

export default function Portfolio() {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(
    () => (filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.sector === filter)),
    [filter]
  );

  return (
    <div data-testid="page-portfolio">
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="overline">Portfolio & Case Studies</div>
            <h1
              data-testid="portfolio-title"
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            >
              A selection of platform investments.
            </h1>
          </div>
          <div className="lg:col-span-6">
            <p className="text-slate-700 text-lg leading-relaxed">
              A representative view of active and historical commitments across
              our core sectors. Specific terms, valuations, and counterparties
              are confidential by policy.
            </p>
          </div>
        </div>
      </section>

      {/* FILTER */}
      <section className="py-6 bg-white border-y border-slate-200 sticky top-20 z-30 backdrop-blur-xl bg-white/85">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-wrap gap-2">
          {SECTORS.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`portfolio-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(s)}
              className={`text-[12px] tracking-[0.18em] uppercase font-semibold px-4 py-2.5 border transition-colors ${
                filter === s
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-900 hover:text-slate-900"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div
            data-testid="portfolio-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200"
          >
            {filtered.map((p, i) => (
              <article
                key={p.title}
                data-testid={`portfolio-card-${i}`}
                className="bg-white p-8 lg:p-10 min-h-[260px] flex flex-col justify-between hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="overline">{p.sector}</div>
                    <span
                      className={`text-[10px] tracking-[0.18em] uppercase font-semibold px-2.5 py-1 border ${
                        STATUS_STYLES[p.status] || STATUS_STYLES.Active
                      }`}
                    >
                      {p.status}
                    </span>
                  </div>
                  <h3 className="mt-4 font-serif text-2xl tracking-tight text-slate-900 leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {p.description}
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                  <MapPin size={14} />
                  <span>{p.geo}</span>
                </div>
              </article>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-20 text-center text-slate-500">
              No investments in this sector currently disclosed.
            </div>
          )}
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-950 text-white">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <div className="overline text-slate-400">Co-investment & Partnership</div>
          <h2 className="mt-6 font-serif text-3xl lg:text-4xl tracking-tight">
            Bring us a thesis. We act with conviction.
          </h2>
          <Link
            to="/opportunity"
            data-testid="portfolio-cta-contact"
            className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-semibold bg-white text-slate-900 px-7 py-4 hover:bg-slate-100 transition-colors"
          >
            Submit an opportunity <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
